import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Copy, Check } from "lucide-react";
import { getBranding } from "@/utils/firmDataService";
import { getAffiliateLink } from "@/utils/tracking";
import { trackEvent } from "@/utils/analytics";
import { AffiliateCTA } from "@/components/ui/AffiliateCTA";

const parseDiscountPct = (discount) => {
  if (!discount) return 0;
  const m = discount.match(/(\d+)%/);
  return m ? parseInt(m[1]) : 0;
};

const DealCard = ({ firm }) => {
  const [copied, setCopied] = useState(false);
  const pc = firm.promo_code || {};
  const branding = getBranding(firm.slug);
  const affiliateUrl = getAffiliateLink(firm) || `/firms/${firm.slug}`;

  const handleCopy = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!pc.code) return;
    navigator.clipboard.writeText(pc.code).then(() => {
      setCopied(true);
      trackEvent("promo_code_copied", {
        firm_name: firm.name,
        firm_slug: firm.slug,
        promo_code: pc.code,
        discount: pc.discount,
        location: "homepage_live_deals",
      });
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [pc.code, pc.discount, firm.name, firm.slug]);

  return (
    <div className="flex-shrink-0 w-[200px] sm:w-[220px] rounded-xl border border-gray-200 dark:border-emerald-500/15 bg-white dark:bg-[#111420] hover:border-emerald-400 dark:hover:border-emerald-500/35 hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition-all duration-200 overflow-hidden flex flex-col" data-testid={`live-deal-${firm.slug}`}>
      <div className="h-[2px] bg-gradient-to-r from-emerald-500 to-emerald-600 opacity-80 dark:opacity-60" />
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/firms/${firm.slug}`} className="flex items-center gap-2.5 mb-3 group">
          <div className="firm-logo-tile w-8 h-8 rounded-lg flex-shrink-0 p-0.5 flex items-center justify-center overflow-hidden">
            {branding?.logo ? (
              <img src={branding.logo} alt={firm.name} width="28" height="28" className="w-full h-full object-contain" onError={e => { e.target.style.display = "none"; }} />
            ) : (
              <span className="text-[10px] font-bold text-white">{firm.icon || firm.name?.slice(0, 2)}</span>
            )}
          </div>
          <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-200 group-hover:text-slate-950 dark:group-hover:text-white transition-colors truncate">{firm.name}</span>
        </Link>

        <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 tracking-tight mb-1.5" data-testid={`deal-discount-${firm.slug}`}>
          {pc.discount || "Special Offer"}
        </div>

        {pc.size_specific && (
          <div className="text-[10px] text-slate-500 mb-2">{pc.size_specific} only</div>
        )}

        {pc.code && (
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 rounded-md px-2.5 py-1.5 mb-2 transition-colors cursor-pointer group self-start"
            title="Click to copy code"
            data-testid={`copy-code-${firm.slug}`}
          >
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 font-mono tracking-wider">{pc.code}</span>
            {copied ? (
              <Check size={12} className="text-emerald-600 dark:text-emerald-400" />
            ) : (
              <Copy size={11} className="text-emerald-400 dark:text-emerald-500/60 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors" />
            )}
          </button>
        )}
        {copied && (
          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium -mt-1.5 mb-2">Copied!</div>
        )}

        <div className="mt-auto">
          <AffiliateCTA
            href={affiliateUrl}
            label="Get Deal"
            firmSlug={firm.slug}
            placement="homepage_live_deals"
            size="sm"
            showSubtext={false}
            data-testid={`get-deal-${firm.slug}`}
          />
        </div>
      </div>
    </div>
  );
};

export const LiveDealsStrip = ({ firms }) => {
  if (!firms?.length) return null;

  const dealsOnly = firms
    .filter(f => f.is_discount_live && f.promo_code && (f.promo_code.discount || f.promo_code.code))
    .map(f => ({ ...f, _discPct: parseDiscountPct(f.promo_code?.discount) }))
    .sort((a, b) => b._discPct - a._discPct);

  if (!dealsOnly.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="live-deals-strip">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight">Live Deals</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">Verified codes — updated daily</span>
        </div>
        <Link to="/futures-prop-firm-discounts" className="text-xs sm:text-sm text-blue-500 hover:text-blue-400 font-medium flex items-center gap-1 transition-colors" data-testid="see-all-deals">
          See all discounts <ArrowRight size={14} />
        </Link>
      </div>

      <div className="flex gap-3 items-stretch overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {dealsOnly.map(firm => (
          <DealCard key={firm.slug} firm={firm} />
        ))}
      </div>
    </section>
  );
};
